import { gql, useMutation } from "@apollo/client";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import LayoutWrapper from "./LayoutWrapper";
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import "brace/mode/html";
import "brace/theme/github";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import AceEditor from "react-ace";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { editCreative, resetEditConfig } from "../reducers/customOrdersReducer";
import { pushMessage } from "../reducers/messagingReducer";
import PaymentRegistrationBanner from "./common/PaymentRegistrationBanner";
import { CREATIVES_DISPLAY_NAMES, LINEITEM_STATS_MUTATION, VIDEO_PROPERTY_MAPPINGS } from "./common/creatives/definitions";
import Cta from "./common/marketplace/CTA";
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import DatePicker from "react-datepicker";
import { GAM_API_MUTATION as CUSTOM_CREATIVES_MUTATION } from './common/creatives/definitions'
import { getMetadata } from "./common/creatives/functions";
import { Paginator } from "primereact/paginator";

/**
 * @typedef {Object} ReportMetrics
 * @property {string} adserverCtr
 * @property {string} adserverImpressions
 * @property {string} clicks
 * @property {string} revenue
 * @property {string} serverRevenue
 * @property {string} totalLineItemImpressions
 * @property {string} viewability
 */

const GAM_API_MUTATION = gql`
    mutation callCustomOrdersMutation($id: String, $page: String) {
        customOrdersMutation(input: { id: $id, page: $page }) {
            ordersInfo,
            creativesMetadata
            crmc_report
        }
    }
`;

const DELETE_ORDERS_MUTATION = gql`
    mutation callDeleteCustomOrderMutation($orderId: String, $gamLineItemId: String) {
        deleteCustomOrderMutation(input: { orderId: $orderId, gamLineItemId: $gamLineItemId }) {
            status
        }
    }
`;

const UPDATE_CREATIVE_CONFIG_MUTATION = gql`
    mutation callUpdateCreativeConfigurationMutation($id: String, $customerId: String, $data: String) {
        updateCreativeConfigurationMutation(input: {id: $id, customerId: $customerId, data: $data}) {
            mutationResult
        }
    }
`

/**
 * @typedef {Object} CreativeDefaultConfig
 * @property {string} t - String that identifies the creative by id (e.g. crmc_id=SOME_ID)
 * @property {string} creativeType
 * @property {string?} videoCreativeType
 * @property {string?} videoProperty
 */

/**
 * @typedef {Object} CreativePostDefinition
 * @property {string} creationDate
 * @property {CreativeDefaultConfig} fields
 * @property {string} id - This refers to the WP Post Id
 * @property {string} modificationDate
 * @property {string} title - This refers to the name of custom creative title/campaign name.
 */

/**
 * @typedef {Object} CreativeMutationFields
 * @property {string} id - This is the customer ID
 * @property {string} customerName
 * @property {string} campaignName
 * @property {string} creativeType - Any of the custom creative types (e.g. display_interstitial)
 * @property {string} creativeJson - JSON-serialized object that represents the creative's configuration
 * @property {string} gamStartDate - Stringified date timestamp that represnets the start of the campaign
 * @property {string} gamEndDate   - Stringified date timestamp that represents the end of the campaign
 * @property {string} impressionGoals - String(number_of_impressions)
 * @property {boolean} isTestOrder
 * @property {string} creativeTypeDisplayName - Creative 'nice name' (e.g. Display Interstitial)
 * @property {string} paymentMethodId - Stripe payment method id associated to customer
 * @property {boolean} isVastCreative - Specifies if this creative is a video creative
 */

let host = process.env?.REACT_APP_SERVER;
let protocol = process.env?.REACT_APP_SERVER_PROTOCOL || 'https'
let cloneDialogStyles = css`
    padding: 1em;
    border-left: 2px solid #2196F3;
    .flex {
        margin-bottom: 1em;
    }
    .creative-detail {
        display: inline-block;
        min-width: 150px;
        margin-right: 1em;
        padding: 0.5rem 0.5rem;
        padding-left: 0;
    }
    .p-inputtext,
    .p-inputnumber {
        border-left: none;
        border-right: none;
        border-top: none;
        flex-grow: 0.5;
        &:enabled:focus:active {
            box-shadow: none;
            border-color: unset;
        }
    }
    .p-inputnumber > .p-inputtext {
        flex-grow: 1;
    }
    .react-datepicker-wrapper {
        flex-grow: 0.5;
        input {
            border: none;
            border-bottom: 1px solid #ced4da;
            font-size: 1rem;
            color: #495057;
            background: #ffffff;
            padding: 0.5rem 0.5rem;
            width: 100%;
            &:focus:active:focus-within {
                border-color: none;
            }
            &[disabled] {
                opacity: 0.6;
            }
        }
    }
`
/**
 *
 * @param {number|string} width
 * @param {number|string} height
 * @param {string} targeting
 * @param {CreativeDefaultConfig} creativeData
 * @returns
 */
function generateOOPCreativeCode(width, height, targeting, creativeData) {
    let [k, v] = targeting.split('=')
    let wrapper = creativeData.container
    let id = Math.random().toString(16).slice(2)
    let isSelectorPresent = !!wrapper

    return (
        `
        <script>
        (function (global, width, height) {
            let top = global.top
            let div = top.document.createElement('div')
            div.id  = 'crmc-${id}'
            div.classList.add("adg-crmc-container")

            if (${isSelectorPresent}) {
                top.document.querySelector('${wrapper}').appendChild(div)
            } else {
                top.document.body.appendChild(div);
                div.classList.add("adg-crmc-int")
                div.classList.add("adg-crmc-${v}")
            }

            let src = "https://securepubads.g.doubleclick.net/tag/js/gpt.js"
            let gtScript = top.document.createElement('script')
            gtScript.src = src
            top.document.body.appendChild(gtScript)
            top.googletag = top.googletag || { cmd: [] }
            top.googletag.cmd.push(function () {
                top.googletag
                    .defineSlot(
                        '/92510019/custom_rich_media_ad_tracker',
                        [${width}, ${height}],
                        'crmc-${id}'
                    )
                    .addService(top.googletag.pubads())
                    .setTargeting('${k}', ['${v}'])
                    .setClickUrl('%%CLICK_URL_UNESC%%');
                top.googletag.enableServices();
                top.googletag.display('crmc-${id}');
            })

        })(
            window,
            ${width},
            ${height}
        )
        </script>`
    )
}

let refactoredCreatives = [
    "display_parallax",
    "display_interstitial",
    "adhesion",
    "desktop_footer",
    "display_skybox",
    "video-interstitial"
];

const generateScriptTag = (id, customerId, adServingType, creativeCodeName, creativeData, usesS3Bucket) => {
    let macros = '';

    switch (adServingType) {
        case '3p':
            macros = "[['%%CLICK_URL_UNESC%%'],[],[],['%%CACHEBUSTER%%']]"
            break
        case 'cc':
            macros = "[['%%CLICK_URL_UNESC%%'],['%%VIEW_URL_UNESC%%'],['%%DEST_URL_UNESC%%'],['%%CACHEBUSTER%%']]"
            break
        case 'hdc':
            macros = "[[],[],[],[]]"
            break
        default:
            macros = "[['%%CLICK_URL_UNESC%%'],['%%VIEW_URL_UNESC%%'],['%%DEST_URL_UNESC%%'],['%%CACHEBUSTER%%']]"

    }

    if (usesS3Bucket) {
        if (refactoredCreatives.includes(creativeCodeName)) {
            return generateOOPCreativeCode(50, 50, creativeData.t, creativeData)
        }
    }

    return (
        `
        <script>
            var adgridOpts = window.adgridOpts || [];
            var creativeConf = {
            crmcId: ${id},
            cid: ${customerId},
            macros: ${macros},
            source: document.currentScript,
            server: "${host}",
            path: "/wp-content/uploads/crmc/library/adgrid-crmc.js"
            };
            adgridOpts.push(creativeConf);
            var adgridScriptTag = document.createElement('script');
            adgridScriptTag.src = "//" + creativeConf.server + creativeConf.path;
            document.body.insertBefore(adgridScriptTag, document.body.firstChild);
        </script>
        `
    );
}

function isEmpty(i) {
    const t = typeof i;
    switch (t) {
        case "string":
            return i.length === 0;
        case "object":
            return Array.isArray(i) ? i.length === 0 : t === null || Object.keys(i).length === 0;
        case "undefined":
            return true;
        default:
            throw new Error(`Arg of type t -> ${t} is not a collection type`);
    }
}

const ORDER_DELETION_OBJECT = { orderId: "", gamLineItemId: "" };
const now = new Date()
const oneDayMillisecs = 24 * 60 * 60 * 1000
const oneDayFromNow = new Date(now.getTime() + oneDayMillisecs)
const twoDaysFromNow = new Date(now.getTime() + (2 * oneDayMillisecs))
const thirtyDaysFromNow = new Date(oneDayFromNow.getTime() + (30 * oneDayMillisecs))
const nowMillis = now.getTime()
const wholeMonth = new Array(30)
    .fill(0)
    .map((_, idx) => {
        return new Date(nowMillis + (idx + 1) * oneDayMillisecs)
    })
const prevMonth = new Array(32)
    .fill(0)
    .map((_, idx) => {
        return new Date(twoDaysFromNow - (idx + 1) * oneDayMillisecs)
    })

/**
 *
 * @param {Object} params
 * @param {CreativePostDetails} params.config
 * @param {boolean} params.publishState
 * @param {string} params.customerName
 * @param {string} params.customerId
 * @param {Function} params.onClone
 * @returns
 */
const CloneDialogBody = ({
    config,
    publishState,
    customerName,
    customerId,
    onClone
}) => {
    const dispatch = useDispatch()
    const [customCreativeCreationMutation] = useMutation(CUSTOM_CREATIVES_MUTATION)
    const [creativeDetails, setCreativeDetails] = useState({
        campaignTitle: config.title + ' (copy)',
        goals: Number(config.fields.impression_goals),
        startDate: Number(config.fields.start_date) < +oneDayFromNow
            ? oneDayFromNow
            : new Date(Number(config.fields.start_date)),
        endDate: Number(config.fields.end_date) < +twoDaysFromNow
            ? thirtyDaysFromNow
            : new Date(Number(config.fields.end_date))
    })

    /**
     *
     * @param {'campaignTitle' | 'goals' | 'startDate' | 'endDate'} field
     * @param {string | number | Date} value
     */
    function editField(field, value) {
        switch (field) {
            case 'campaignTitle':
                setCreativeDetails({ ...creativeDetails, [field]: value.target.value })
                break;
            case 'goals':
                setCreativeDetails({ ...creativeDetails, [field]: value.value })
                break;
            case 'startDate':
                /** @type {Date} */
                let d0 = value
                let t0 = d0.getTime()
                let oneDayFromNowMillis = oneDayFromNow.getTime()
                if ((t0 - oneDayFromNowMillis >= 0) && t0 < creativeDetails.endDate.getTime()) {
                    setCreativeDetails({ ...creativeDetails, [field]: d0 })
                } else {
                    setCreativeDetails({ ...creativeDetails, [field]: oneDayFromNow })
                    dispatch(pushMessage("Error", "Date must be in the future and earlier than campaign end date", "error"))
                }
                break;
            case 'endDate':
                /** @type {Date} */
                let d = value
                let diff = d.getTime() - twoDaysFromNow.getTime()

                if (diff > oneDayMillisecs || diff <= 0) {
                    setCreativeDetails({ ...creativeDetails, [field]: value })
                } else {
                    setCreativeDetails({ ...creativeDetails, [field]: twoDaysFromNow })
                    dispatch(pushMessage('Creative cloning', 'Date must be at least 2 days in the future', 'error'))
                }
                break
            default:
                break;
        }
    }

    useEffect(() => {
        if (publishState) {
            /** @type {CreativeDefaultConfig} */
            let creativeConfig = JSON.parse(config.fields.creative_json_config)
            let isVideoCreative = !!creativeConfig.videoCreativeType

            delete creativeConfig.t
            creativeConfig.cpm = config.fields.cpm

            if (creativeConfig.creativeType === 'cube') {
                let sideTypes = ['side1Type', 'side2Type', 'side3Type', 'side4Type']
                let isDisplay = sideTypes.every(st => creativeConfig[st] === 'display')

                if (isDisplay) {
                    constructCreativePayload()
                } else {
                    delete creativeConfig.vast1
                    delete creativeConfig.vast2
                    delete creativeConfig.vast3
                    delete creativeConfig.vast4

                    let vastRegex = /vast|gampad|pubads|doubleverify|ad\.doubleclick\.net|rtr\.innovid\.com/;
                    let isVastCreative = sideTypes.some(st => creativeConfig[st] === 'vast')
                    let sidesMetadata = []
                    let cubeVideoAdUnits = []
                    let sides = ["side1", "side2", "side3", "side4"]
                    let adUnitPrefix = "crmc_video_cube_"

                    creativeConfig.isVideoCreative = true
                    creativeConfig.isVastCreative = isVastCreative

                    if (isVastCreative) {
                        sides.forEach((side, sideIdx) => {
                            let url = creativeConfig[side]
                            let currIdx = sideIdx + 1
                            let adUnit = adUnitPrefix + currIdx
                            if (url && vastRegex.test(url)) {
                                sidesMetadata.push({
                                    vastProperty: 'vast' + currIdx,
                                    videoUrl: url,
                                    tau: adUnit,
                                    duration: creativeConfig['vast' + currIdx + 'Duration']
                                })
                                cubeVideoAdUnits.push(adUnit)
                            }
                        })
                        creativeConfig.cubeVideoAdUnits = cubeVideoAdUnits
                        creativeConfig.videoCreativeMetadata = sidesMetadata
                        constructCreativePayload()
                    } else {
                        // if we reach here, it means that some of the video creatives were uploaded by the user.
                        let videoSides = sides.filter(side => creativeConfig[side + 'Type'] !== 'display')
                        let metadataPromises = videoSides.map(side => {
                            return getMetadata(creativeConfig[side])
                        })

                        Promise
                            .all(metadataPromises)
                            .then(values => {
                                values.forEach((obj, idx) => {
                                    let duration = parseInt(obj.duration)
                                    let byteLength = parseInt(obj.contentLength)
                                    let adUnit = adUnitPrefix + videoSides[idx].at(-1)
                                    sidesMetadata.push({
                                        videoUrl: creativeConfig[videoSides[idx]],
                                        duration,
                                        byteLength,
                                        bitrate: Math.floor(byteLength / duration),
                                        vastProperty: "vast" + videoSides[idx].at(-1),
                                        tau: adUnit
                                    })
                                    cubeVideoAdUnits.push(adUnit)
                                })
                                creativeConfig.cubeVideoAdUnits = cubeVideoAdUnits
                                creativeConfig.videoCreativeMetadata = sidesMetadata

                                constructCreativePayload()
                            })
                            .catch(err => {
                                console.log({ err })
                            })
                    }
                    // constructCreativePayload()
                }

            } else if (isVideoCreative && creativeConfig.videoCreativeType === 'video') {
                let videoProperty = VIDEO_PROPERTY_MAPPINGS[creativeConfig.creativeType]?.videoProperty
                let tau = VIDEO_PROPERTY_MAPPINGS[creativeConfig.creativeType]?.tau

                creativeConfig.videoProperty = videoProperty
                creativeConfig.tau = tau
                creativeConfig.isVideoCreative = true
                delete creativeConfig.vast

                getMetadata(creativeConfig[videoProperty])
                    .then(response => {
                        let duration = parseInt(response.duration)
                        let byteLength = parseInt(response.contentLength)

                        creativeConfig.duration = duration
                        creativeConfig.bitrate = Math.floor(byteLength / duration)

                        return constructCreativePayload()
                    })
                    .catch(err => {
                        console.log(err)
                    })
            } else if (isVideoCreative && creativeConfig.videoCreativeType === 'vast') {
                let videoProperty = VIDEO_PROPERTY_MAPPINGS[creativeConfig.creativeType]?.videoProperty
                let tau = VIDEO_PROPERTY_MAPPINGS[creativeConfig.creativeType]?.tau

                creativeConfig.videoProperty = videoProperty
                creativeConfig.tau = tau
                creativeConfig.isVideoCreative = true
                delete creativeConfig.vast

                constructCreativePayload()
            } else {
                constructCreativePayload()
            }

            function constructCreativePayload() {
                /** @type {CreativeMutationFields} */
                let payload = {}
                payload.id = customerId
                payload.customerName = customerName
                payload.campaignName = creativeDetails.campaignTitle
                payload.creativeType = creativeConfig.creativeType
                payload.gamStartDate = String(+creativeDetails.startDate / 1000)
                payload.gamEndDate = String(+creativeDetails.endDate / 1000)
                payload.impressionGoals = config.fields.impression_goals
                payload.isTestOrder = process.env.REACT_APP_SERVER !== 'console.adgrid.io'
                payload.creativeTypeDisplayName = CREATIVES_DISPLAY_NAMES[creativeConfig.creativeType]
                payload.paymentMethodId = config.fields.payment_method_id
                payload.isVastCreative = ['side1Type', 'side2Type', 'side3Type', 'side4Type']
                    .some(sideType => creativeConfig[sideType] === 'vast') || !!creativeConfig.vastUrl

                payload.creativeJson = JSON.stringify(creativeConfig)
                payload.creativeWidth = 50
                payload.creativeHeight = 50

                customCreativeCreationMutation({
                    variables: payload
                }).then(response => {
                    onClone(response)
                }).catch(err => {
                    console.log(err)
                })
            }


        }
    }, [publishState])

    return (
        <div className="flex flex-col flex-center">
            <div className="mb-1" css={cloneDialogStyles}>
                <h5>
                    <b>Creative Details</b>
                </h5>
                <div className="flex">
                    <b className="creative-detail"><i className="pi pi-info-circle"></i> Title</b>
                    <InputText
                        disabled={publishState}
                        value={creativeDetails.campaignTitle}
                        onChange={(ev) => editField('campaignTitle', ev)}
                    />
                </div>
                <div className="flex">
                    <b className="creative-detail"><i className="pi pi-eye"></i> Impression goals</b>
                    <InputNumber
                        disabled={publishState}
                        value={creativeDetails.goals}
                        onChange={(ev) => editField('goals', ev)}
                    />
                </div>
                <div className="flex">
                    <b className="creative-detail"><i className="pi pi-calendar"></i> Start date</b>
                    <DatePicker
                        disabled={publishState}
                        selected={creativeDetails.startDate}
                        onChange={(ev) => editField('startDate', ev)}
                        includeDates={wholeMonth}
                    />
                </div>
                <div className="flex">
                    <b className="creative-detail"><i className="pi pi-calendar"></i> End date</b>
                    <DatePicker
                        disabled={publishState}
                        selected={creativeDetails.endDate}
                        onChange={(ev) => editField('endDate', ev)}
                        excludeDates={prevMonth}
                    />
                </div>
            </div>
            {
                !publishState && (
                    <p style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        Clicking the "Accept" button will clone this creative and publish it.
                    </p>
                )
            }
        </div>
    )
}

const CreativeOrders = () => {
    /** @type {{currentCustomer: String, isAdmin: boolean, customers: {name: string, value: string}[]}} */
    const { currentCustomer, isAdmin, customers } = useSelector((state) => state.userInformation);
    const { customerToStripeAccountMappings, infoAvailable: stripeInfoAvailable } = useSelector((state) => state.stripeInfo);
    const dispatch = useDispatch();
    const [gamApiMutationContainer] = useMutation(GAM_API_MUTATION);
    const [deleteOrderMutationContainer] = useMutation(DELETE_ORDERS_MUTATION);
    const [lineItemStatsMutationContainer] = useMutation(LINEITEM_STATS_MUTATION);
    const [updateCreativeConfigContainer] = useMutation(UPDATE_CREATIVE_CONFIG_MUTATION);
    const [orderDeletionInfo, setOrderDeletionInfo] = useState(ORDER_DELETION_OBJECT);
    const [ordersInfo, setOrdersInfo] = useState({});
    const [isOrdersEmpty, setIsOrdersEmpty] = useState(false);
    const [displayScriptTagOverlay, setDisplayScriptOverlay] = useState(false);
    const [scriptTagCode, setScriptTagCode] = useState("");
    const [isConfirmDeletionVisible, setConfirmDeletionVisibility] = useState(false);
    const history = useHistory();
    const [lineItemStats, setLineItemStats] = useState({});
    const [hasPaymentMethods, setHasPaymentMethods] = useState(false);
    const [displayPaymentMissingSection, setDisplayPaymentSection] = useState(false);
    const [displayCloneDialog, setDisplayCloneDialog] = useState(false)
    /** @type {[CreativePostDetails, React.Dispatch<React.SetStateAction<CreativePostDetails>>]} */
    const [orderToCloneConfig, setOrderToCloneConfig] = useState(null)
    /** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
    const [isCloningCreative, setCreativeCloningState] = useState(false)
    /** @type {[CreativeMutationFields, React.Dispatch<React.SetStateAction<CreativeMutationFields>>]} */
    /** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
    const [customerName, setCustomerName] = useState(null)
    const [showLoader, setShowLoader] = useState(false)
    const [paginationInfo, setPaginationInfo] = useState({})
    const [reportInformation, setReportInformation] = useState({})

    async function updateCreativeConfiguration({ creativeId, customerId, creativeJsonData }) {
        let response = await updateCreativeConfigContainer({
            variables: {
                customerId,
                id: creativeId,
                data: creativeJsonData,
            }
        })
        return response;
    }

    async function requestCustomersCustomCreatives(page = 1) {
        setShowLoader(true)
        let res = await gamApiMutationContainer({ variables: { id: currentCustomer, page: String(page) } })

        try {
            let ordersInfo = res.data.customOrdersMutation.ordersInfo
            let creativesMetadata = JSON.parse(res.data.customOrdersMutation.creativesMetadata || '{}')
            /** @type {string} */
            let creativeReport = res.data.customOrdersMutation.crmc_report

            if (creativeReport) {
                let [headerLine, ...restLines] = creativeReport.split('\n')
                let report = restLines
                    .reduce((acc, curr) => {
                        let [adUnitId, adUnitName, lineItemId, adserverImpressions, totalLineItemImpressions, revenue, serverRevenue, ctr, adserverCtr, clicks, viewability] = curr.split(',')
                        acc[lineItemId] = {
                            adserverImpressions,
                            totalLineItemImpressions,
                            revenue,
                            serverRevenue,
                            ctr,
                            adserverCtr,
                            clicks,
                            viewability
                        }
                        return acc
                    }, {})
                setReportInformation(report)
            }

            setPaginationInfo(creativesMetadata)
            let lineItemIds = [];
            ordersInfo = ordersInfo ? JSON.parse(ordersInfo) : {};
            setIsOrdersEmpty(isEmpty(ordersInfo.posts_info));
            if (ordersInfo.posts_info && Array.isArray(ordersInfo.posts_info)) {
                ordersInfo.posts_info.sort((a, b) => {
                    let orderA = parseInt(a.id);
                    let orderB = parseInt(b.id);
                    return orderB - orderA;
                });
                lineItemIds = ordersInfo.posts_info.map((i) => i?.fields?.line_item_id);
            }
            setOrdersInfo(ordersInfo);

            let lineItemMutationStatsResponse = await lineItemStatsMutationContainer({
                variables: {
                    lineItemIdsList: JSON.stringify(lineItemIds)
                }
            })
            let stats = JSON.parse(lineItemMutationStatsResponse.data.getLineItemStatsMutation.gamApiStats || "{}")
            setLineItemStats(stats)
            setShowLoader(false)
        } catch (err) {
            dispatch(pushMessage("Creative orders error", err.message, "error"))
            setShowLoader(false)
        }
    }

    useEffect(() => {
        if (currentCustomer) {
            setCustomerName(
                customers.find(item => item.value === currentCustomer)?.name
            )
        }
    }, [currentCustomer, customers])

    useEffect(() => {
        if (stripeInfoAvailable) {
            setHasPaymentMethods(!!customerToStripeAccountMappings?.[currentCustomer]);
        }
    }, [customerToStripeAccountMappings, stripeInfoAvailable, currentCustomer]);

    useEffect(() => {
        if (currentCustomer) {
            requestCustomersCustomCreatives()
        }
    }, [currentCustomer]);

    const renderCreativeScriptTag = (id, customerId, adServingType, creativeType, creativeData, usesS3Bucket) => {
        setScriptTagCode(generateScriptTag(id, customerId, adServingType, creativeType, creativeData, usesS3Bucket));
    };

    const deleteWpOrder = (orderId, gamLineItemId) => {
        deleteOrderMutationContainer({
            variables: {
                orderId,
                gamLineItemId,
            },
        })
            .then(() => {
                dispatch(pushMessage("Creative Orders", "Custom order successfully deleted. ID was " + orderId));
                setOrderDeletionInfo(ORDER_DELETION_OBJECT);
                setOrdersInfo({
                    posts_info: ordersInfo?.posts_info.filter(({ id }) => id !== orderId),
                });
            })
            .catch(() => {
                dispatch(pushMessage("Creative Orders", "There was an error while trying to delete your custom order", "error"));
                setOrderDeletionInfo(ORDER_DELETION_OBJECT);
            });
    };

    const renderTableControls = (rowData) => {
        const config = JSON.parse(rowData.fields.creative_json_config);

        let previewEndpoint = rowData.fields.uses_s3_bucket
            ? `${protocol}://${host}/wp-content/uploads/crmc/library/creatives/preview.html?s=${host}&crmcId=${rowData.id}&cid=${rowData?.fields?.customer_id}&debug=true&s3=true&preview=true&bucket=${process.env.REACT_APP_ENV}`
            : `${protocol}://${host}/wp-content/uploads/crmc/library/creatives/preview.html?s=${host}&crmcId=${rowData.id}&cid=${rowData?.fields?.customer_id}&debug=true&preview=true`

        return (
            <div css={css`
                display: flex;
                justify-content: space-between;
            `}>
                <Button
                    tooltip="Preview creative"
                    className="p-button p-component p-button-rounded p-button-success p-button-icon-only p-button-outlined"
                    icon="pi pi-external-link"
                    tooltipOptions={{ position: 'bottom' }}
                    onClick={ev => {
                        ev.currentTarget.querySelector('.preview')?.click()
                    }}
                >
                    <a
                        className="preview"
                        target="_blank"
                        href={previewEndpoint}
                    >
                    </a>
                </Button>
                <Button
                    tooltip="Edit creative"
                    onClick={() => {
                        editCreativeConfig(rowData, config)
                    }}
                    className="p-button p-component p-button-rounded p-button-plain p-button-icon-only p-button-outlined"
                    tooltipOptions={{ position: 'bottom' }}
                    icon="pi pi-pencil"
                />
                <Button
                    tooltip={rowData.fields?.is_creative_enabled
                        ? "Disable creative"
                        : "Enable creative"
                    }
                    onClick={() => toggleCreativeStatus(rowData, config)}
                    className="p-button p-component p-button-rounded p-button-danger p-button-icon-only p-button-outlined"
                    tooltipOptions={{ position: 'bottom' }}
                    icon={rowData.fields?.is_creative_enabled ? 'pi pi-ban' : 'pi pi-check'}
                />
                <Button
                    tooltip="View ad code"
                    className="p-button p-component p-button-rounded p-button-info p-button-icon-only p-button-outlined"
                    icon="pi pi-info"
                    tooltipOptions={{ position: 'bottom' }}
                    onClick={() => showAdCode(rowData, config)}
                />
                <Button
                    tooltip="Clone creative"
                    className="p-button p-component p-button-rounded p-button-info p-button-icon-only p-button-outlined"
                    icon="pi pi-clone"
                    tooltipOptions={{ position: 'bottom' }}
                    onClick={() => {
                        setDisplayCloneDialog(true)
                        setOrderToCloneConfig(rowData)
                    }}
                />
            </div>
        );
    };

    /**
     *
     * @param {CreativePostDefinition} rowData
     *
     */
    const creativeTypeColumn = (rowData) => {
        const config = JSON.parse(rowData.fields.creative_json_config);
        let creativeType = CREATIVES_DISPLAY_NAMES[config.creativeType];
        return (
            <p>
                <strong>{creativeType}</strong>
            </p>
        );
    };

    /**
     *
     * @param {CreativePostDefinition} rowData
     *
     */
    const creativeOrderNameColumn = (rowData) => {
        let creativeTitle = rowData.title
        return (
            <p>
                <strong>{creativeTitle}</strong>
            </p>
        );
    };


    /**
     *
     * @param {CreativePostDefinition} rowData
     *
     */
    const viewabilityPercentageColumn = (rowData) => {
        let lineItemId = rowData.fields.line_item_id
        let usesS3Bucket = rowData.fields.uses_s3_bucket
        /** @type {ReportMetrics | null} */
        let reportInfo = reportInformation[lineItemId]
        let viewabilityPercentage = 'NA'

        if (reportInfo && usesS3Bucket) {
            let viewability = parseFloat(reportInfo.viewability)
            viewabilityPercentage = viewability * 100 + '%'
        }

        return (
            <p>
                <strong>{viewabilityPercentage}</strong>
            </p>
        );
    };

    /**
     *
     * @param {CreativePostDefinition} rowData
     *
     */
    const ctrColumn = (rowData) => {
        let lineItemId = rowData.fields.line_item_id
        let usesS3Bucket = rowData.fields.uses_s3_bucket

        /** @type {ReportMetrics | null} */
        let reportInfo = reportInformation[lineItemId]
        let ctr = 'NA'
        if (reportInfo && usesS3Bucket) {
            ctr = parseFloat(reportInfo.adserverCtr)
        }
        return (
            <p>
                <strong>{ctr}</strong>
            </p>
        );
    };

    const columnDate = (rowData, key) => {
        let d = new Date(parseInt(rowData.fields[key]));
        d = d.toLocaleDateString();
        return <p>{d}</p>;
    };

    const startDateColumn = (rowData) => columnDate(rowData, "start_date");
    const endDateColumn = (rowData) => columnDate(rowData, "end_date");

    const statusColumn = (rowData) => {
        const label = !!rowData?.fields?.is_creative_enabled;
        return (
            <span
                css={css`
                    color: ${label ? "green" : "red"};
                `}
            >
                {label ? "Active" : "Inactive"}
            </span>
        );
    };

    const statsColumn = (rowData) => {
        let lineItemId = rowData?.fields?.line_item_id;
        let stats = lineItemStats[lineItemId] ?? {};
        let impressionsDelivered = stats.impressionsDelivered || 0;
        return parseInt(impressionsDelivered).toLocaleString();
    };

    const ongoingChargeColumn = (rowData) => {
        let cpm = parseFloat(rowData?.fields?.cpm);
        let lineItemId = rowData?.fields?.line_item_id;
        let stats = lineItemStats[lineItemId] ?? {};
        let impressionsDelivered = parseInt(stats.impressionsDelivered || 0);
        return "$" + parseFloat(cpm * impressionsDelivered * 0.001).toFixed(2);
    };

    const navigateToCreativesScreen = () => {
        dispatch(resetEditConfig());
        if (!hasPaymentMethods) {
            setDisplayPaymentSection(true);
            return;
        }
        history.push("/creatives");
    }

    const editCreativeConfig = (rowData, config) => {
        const { start_date, end_date, impression_goals, line_item_id, cpm, is_creative_enabled: enabled } = rowData.fields;

        if (!enabled) {
            dispatch(pushMessage('Custom creatives', "This creative has been disabled by an administrator", "error"))
            return
        }

        /* If this is an old-format creative and this creative type hasn't been refactored to use the new format, don't allow the user to edit it */
        if (!rowData.fields.uses_s3_bucket && refactoredCreatives.includes(config.creativeType)) {
            dispatch(pushMessage('Custom creatives', "This creative uses and old format that can't be edited anymore. Please create a new creative or clone this one.", "error"))
            return
        }

        if (!isAdmin && !enabled) {
            dispatch(pushMessage("Creative Orders", "This order has been disabled by an administrator and therefore cannot be edited. To enable this order again get in contact with support", "error"));
            return;
        }

        dispatch(
            editCreative(
                config.creativeType,
                rowData.id,
                config,
                { startDate: start_date, endDate: end_date, orderName: rowData.title, impressionGoals: impression_goals, lineItemId: line_item_id },
                currentCustomer,
                rowData?.fields?.payment_method_id || "",
                lineItemStats[line_item_id],
                cpm
            )
        );

        history.push("/creatives");
    }

    const toggleCreativeStatus = async (rowData, config) => {
        const { is_creative_enabled: enabled } = rowData.fields;
        config.enabled = !enabled;
        let creativeJsonData = JSON.stringify(config);

        let response = await updateCreativeConfiguration({
            customerId: String(currentCustomer),
            creativeId: rowData.id,
            creativeJsonData,
        })

        let data = JSON.parse(response.data.updateCreativeConfigurationMutation.mutationResult)

        if (data.succeeded) {
            let posts_info = ordersInfo.posts_info.map(creativeOrder => {
                if (creativeOrder.id === rowData.id) {
                    return {
                        ...creativeOrder,
                        fields: {
                            ...creativeOrder.fields,
                            creative_json_config: creativeJsonData,
                            is_creative_enabled: !enabled
                        }
                    }
                }
                return creativeOrder
            })
            setOrdersInfo({ ...ordersInfo, posts_info })
        }
    }

    /**
     *
     * @param {CreativePostDefinition} rowData
     * @param {CreativeDefaultConfig} config
     * @returns
     */
    const showAdCode = (rowData, config) => {
        const { is_creative_enabled: enabled } = rowData.fields;
        if (!enabled) {
            dispatch(pushMessage('Custom creatives', "This creative has been disabled by an administrator", "error"))
            return
        }
        if (!isAdmin && !rowData.fields.is_creative_enabled) {
            dispatch(pushMessage("Creative Orders", "You cannot view this order configuration anymore. Get in contact with support", "error"));
            return;
        }
        renderCreativeScriptTag(
            parseInt(rowData.id),
            currentCustomer,
            config.adServingType,
            config.creativeType,
            config,
            rowData.fields.uses_s3_bucket
        );
        setDisplayScriptOverlay(true);
    }

    const CreativeCodeDialogHeader = () => {
        return (
            <>
                <p>Creative script tag:</p>
                {isAdmin && (
                    <>
                        <p>
                            Creative library full path: &nbsp;
                            <a href={`${protocol}://${host}/wp-content/uploads/crmc/library/adgrid-crmc.js`} target="_blank" rel="noreferrer">
                                {protocol}://{host}/wp-content/uploads/crmc/library/adgrid-crmc.js
                            </a>
                        </p>
                    </>
                )}
            </>
        )
    }

    return (
        <>
            <LayoutWrapper model="creative" renderOpsBar={false}>
                <PaymentRegistrationBanner
                    isVisible={displayPaymentMissingSection}
                    onHide={() => setDisplayPaymentSection(false)}
                    headerMessage={
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                                p {
                                    flex-basis: 100%;
                                    text-align: center;
                                }
                                .pi {
                                    font-size: 2em;
                                    color: red;
                                }
                            `}
                        >
                            <div>
                                <i className="pi pi-exclamation-triangle"></i>
                            </div>
                            <p>Register a payment method</p>
                        </div>
                    }
                >
                    <div
                        css={css`
                            padding: 1em 0px;
                            border-radius: 3px;
                            > p {
                                font-size: 1.25em;
                            }
                            a {
                                color: red;
                                border-bottom: 2px solid red;
                            }
                        `}
                    >
                        <p>
                            In order to publish a creative you first need to register a payment method under <Link to="/billing">our Billing page</Link>. After registering a payment method you will be able to publish your custom creatives.
                        </p>
                    </div>
                </PaymentRegistrationBanner>
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        align-items: center;
                    `}
                >
                    <div>
                        <h1>Orders</h1>
                    </div>
                    <Cta
                        ctaText="Add a new order"
                        icon={faCirclePlus}
                        onClick={navigateToCreativesScreen}
                        themeClasses="marketplace-cta default outlined"
                    />
                </div>
                <Dialog visible={isConfirmDeletionVisible} onHide={() => setConfirmDeletionVisibility(false)} style={{ width: "50vw" }} header="Order deletion confirmation" icon="pi pi-exclamation-triangle">
                    <p style={{ fontSize: "16px" }}>Deleting this order will delete all the custom creatives and code associated with this and therefore your GAM instance of this order will not work anymore.</p>
                    <div
                        css={css`
                            display: flex;
                            justify-content: flex-end;
                            > button {
                                margin: 0 6px;
                            }
                        `}
                    >
                        <Button
                            label="Delete order"
                            className="p-button-success"
                            onClick={() => {
                                setConfirmDeletionVisibility(false);
                                deleteWpOrder(orderDeletionInfo.orderId, orderDeletionInfo.gamLineItemId);
                            }}
                        />
                        <Button
                            label="Cancel"
                            className="p-button-secondary"
                            onClick={() => {
                                setConfirmDeletionVisibility(false);
                                setOrderDeletionInfo(ORDER_DELETION_OBJECT);
                            }}
                        />
                    </div>
                </Dialog>

                <Dialog
                    header={
                        <CreativeCodeDialogHeader />
                    }
                    visible={displayScriptTagOverlay}
                    style={{ width: "70vw" }}
                    modal={true}
                    onHide={() => {
                        setDisplayScriptOverlay(false);
                    }}
                >
                    <AceEditor height="300px" width="100%" mode="html" theme="github" showGutter={true} showPrintMargin={false} value={scriptTagCode} editorProps={{ $blockScrolling: true }} fontSize={16} name="gam_script_tag" />
                </Dialog>

                <Dialog
                    header={
                        <>
                            <p>Custom Creative Clone</p>
                        </>
                    }
                    modal={true}
                    closable={false}
                    style={{ width: '50vw' }}
                    visible={displayCloneDialog}
                    onHide={() => {
                        setDisplayCloneDialog(false)
                        setOrderToCloneConfig(null)
                    }}
                >

                    {
                        orderToCloneConfig
                            ? (
                                <CloneDialogBody
                                    config={orderToCloneConfig}
                                    publishState={isCloningCreative}
                                    customerName={customerName}
                                    customerId={currentCustomer}
                                    onClone={function () {
                                        setCreativeCloningState(false)
                                        setDisplayCloneDialog(false)
                                        setOrderToCloneConfig(null)
                                        alert('Cloning finished!')
                                        requestCustomersCustomCreatives()
                                    }}
                                />
                            )
                            : null
                    }

                    <div
                        className="flex flex-row flex-center mt-1"
                        css={css`
                            button {
                                margin: 0 0.25em;
                            }
                        `}
                    >
                        {
                            isCloningCreative
                                ? (
                                    <div css={css`
                                        display: flex;
                                        align-items: center;
                                        color: #689f38;
                                        span {
                                            display: inline-block;
                                            margin-right: 1em;
                                        }
                                    `}>
                                        <span>Cloning creative. Please wait a moment. </span>
                                        <i className="pi pi-spin pi-spinner" style={{ 'fontSize': '2em' }}></i>
                                    </div>
                                )
                                : <>

                                    <Cta
                                        ctaText={"Accept"}
                                        themeClasses="cta outlined default"
                                        onClick={() => {
                                            setCreativeCloningState(true)
                                        }}
                                    />
                                    <Cta
                                        ctaText={"Cancel"}
                                        themeClasses="cta outlined danger"
                                        onClick={() => {
                                            setDisplayCloneDialog(false)
                                            setOrderToCloneConfig(null)

                                        }}
                                    />
                                </>
                        }
                    </div>
                </Dialog>

                {ordersInfo.posts_info && (
                    <DataTable
                        value={ordersInfo.posts_info}
                        css={css`
                            table {
                                table-layout: auto;
                                td.order-name, .creative-type {
                                    max-width: 360px;
                                    width: 250px;        /* set a fixed width */
                                    word-wrap: break-word; /* for legacy support */
                                    word-break: break-word; /* modern support */
                                    overflow-wrap: break-word; /* modern support */
                                    white-space: normal;   /* allow wrapping */
                                }
                                td.creative-type {
                                    width: 150px;
                                }
                            }
                        `}
                    >
                        <Column field="id" header="Order ID" className="order-id"></Column>
                        <Column body={creativeOrderNameColumn} header="Order name" className="order-name"></Column>
                        <Column header="Creative type" className="creative-type" body={creativeTypeColumn}></Column>
                        <Column header="Start date" body={startDateColumn} className="start-date"></Column>
                        <Column header="End date" body={endDateColumn} className="end-date" bodyStyle={{ maxWidth: "50px" }}></Column>
                        <Column header="Impressions" body={statsColumn} bodyStyle={{ maxWidth: "50px" }}></Column>
                        <Column header="CTR" body={ctrColumn} bodyStyle={{ maxWidth: "50px" }}></Column>
                        <Column header="Viewability %" className="viewability-col" body={viewabilityPercentageColumn} bodyStyle={{ maxWidth: "50px" }}></Column>
                        <Column header="Ongoing charge" body={ongoingChargeColumn} bodyStyle={{ maxWidth: "50px" }}></Column>
                        <Column header="Status" body={statusColumn} className="status" colSpan={1}></Column>
                        <Column header="Actions" body={renderTableControls}></Column>
                    </DataTable>
                )}

                {isOrdersEmpty && (
                    <div
                        css={css`
                            background-color: #f3edbe;
                            border-left: 6px solid #f44333;
                            padding: 3em 1em;
                            border-radius: 6px;
                            max-width: 768px;
                            margin-top: 2em;
                            > p {
                                font-size: 1.5em;
                            }
                        `}
                    >
                        <p>
                            You haven't created any
                            <button
                                onClick={navigateToCreativesScreen}
                                css={css`
                                    border: none;
                                    text-decoration: underline;
                                    color: blue;
                                    cursor: pointer;
                                    font-size: 1em;
                                `}
                            >custom creatives</button>
                            at the moment
                        </p>
                    </div>
                )}

                <Paginator
                    rows={20}
                    totalRecords={paginationInfo.total_posts || 0}
                    pageLinkSize={10}
                    first={1}
                    onPageChange={async ev => {
                        let { page, first, rows, pageCount } = ev
                        await requestCustomersCustomCreatives(page + 1)
                    }}
                    css={css`
                        .p-highlight {
                            background: unset !important;
                            color: unset !important;
                            border-color: unset !important;
                        }
                        .p-paginator-pages .p-paginator-element:nth-of-type(${paginationInfo.current_page}) {
                            background: #E3F2FD !important;
                            border-color: #E3F2FD !important;
                            color: #495057 !important;
                        }
                    `}
                ></Paginator>
                <div className={showLoader ? 'data-load-style flex' : 'd-none'}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "3em", color: "#fff" }}></i>
                    <p className="message">Retrieving custom creatives data</p>
                </div>
            </LayoutWrapper>
        </>
    );
};

export default CreativeOrders;
